import { useTranslation } from 'react-i18next';
import { ScanLine } from 'lucide-react';
import type { RecentClimbScan } from '../../../types/climb-scan';
import { RecentPictureRow } from './ClimbRecentPhotos';

interface ClimbRecentScansProps {
  scans: RecentClimbScan[];
  onPick: (scan: RecentClimbScan) => void;
  /**
   * Whether the holds a scan found are of any use to the climb being put up. A
   * boulder is chosen from them, so each wall says how many it has; a route is
   * one line drawn over the wall, and to a route a scan is a photograph.
   */
  offersHolds: boolean;
}

/**
 * The walls this gym has had scanned, as a row to swipe through beside its last
 * climbs' photos.
 *
 * The same row as those (`RecentPictureRow`), and one thing more on the
 * thumbnail: how many holds the scan found, where they will be offered. That is
 * what tells a wall that comes with its holds from one the scan has not looked
 * at yet — the two look the same, and only one of them is a choice rather than
 * a drawing once it is picked.
 */
export function ClimbRecentScans({ scans, onPick, offersHolds }: ClimbRecentScansProps) {
  const { t, i18n } = useTranslation();
  const day = new Intl.DateTimeFormat(i18n.language, { day: 'numeric', month: 'long' });

  return (
    <RecentPictureRow
      title={t('climbEditor.photo.scans')}
      testId="climb-recent-scans"
      pictures={scans.map((scan) => {
        const holds = offersHolds && scan.holds_count > 0
          ? t('climbEditor.photo.scanHolds', { count: scan.holds_count })
          : null;
        const taken = scan.created_at
          ? t('climbEditor.photo.scanOf', { date: day.format(new Date(scan.created_at)) })
          : t('climbEditor.photo.scans');

        return {
          key: scan.scan_id,
          label: holds ? `${taken}, ${holds}` : taken,
          thumbnailUrl: scan.thumbnail_url || scan.url,
          onPick: () => onPick(scan),
          badge: holds && (
            <span
              aria-hidden
              data-testid="climb-recent-scan-holds"
              className="absolute bottom-1 left-1 flex items-center gap-1 rounded-full bg-black/60 px-1.5 py-0.5 text-[10px] font-medium text-white"
            >
              <ScanLine className="size-3" />
              {holds}
            </span>
          ),
        };
      })}
    />
  );
}
